import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Fab from "@mui/material/Fab";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import PostAddIcon from "@mui/icons-material/PostAdd";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AuthContext from "../auth/AuthContext";
import PostFormDialog from "../components/PostFormDialog";
import PostList from "../components/PostList";
import { createPost, getPublishedPosts } from "../utils/apiClient";
import useDocumentTitle from "../hooks/useDocumentTitle";

function Home() {
  useDocumentTitle("");
  const { user } = useContext(AuthContext);
  const isAdmin = !!user?.is_admin;
  const [searchParams, setSearchParams] = useSearchParams();

  const author = searchParams.get("author") || "";
  const tag = searchParams.get("tag") || "";
  const q = searchParams.get("q") || "";

  const [searchInput, setSearchInput] = useState(q);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  const queryParams = useMemo(() => {
    const params = {};
    if (author) params.author = author;
    if (tag) params.tag = tag;
    if (q) params.q = q;
    return params;
  }, [author, tag, q]);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getPublishedPosts(queryParams);
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Failed to load posts");
    } finally {
      setLoading(false);
    }
  }, [queryParams, user?.user_uuid]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    updateParam("q", searchInput.trim());
  };

  const clearFilters = () => {
    setSearchParams({}, { replace: true });
    setSearchInput("");
  };

  const handleCreate = async (payload) => {
    await createPost(payload);
    setCreateOpen(false);
    await loadPosts();
  };

  const hasFilters = Boolean(author || tag || q);

  return (
    <Box sx={{ position: "relative", py: { xs: 1, sm: 2 } }}>
      <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 2 }}>
        <TextField
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Søg indlæg"
          fullWidth
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Ryd søgning"
                  onClick={() => {
                    setSearchInput("");
                    updateParam("q", "");
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {hasFilters ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {author ? (
            <Chip
              label={`Forfatter: ${author}`}
              onDelete={() => updateParam("author", "")}
              size="small"
            />
          ) : null}
          {tag ? (
            <Chip
              component={RouterLink}
              to={`/categories?tag=${encodeURIComponent(tag)}`}
              clickable
              label={`Tag: ${tag}`}
              onDelete={() => updateParam("tag", "")}
              size="small"
              color="primary"
              variant="outlined"
            />
          ) : null}
          {q ? <Chip label={`Søgning: ${q}`} onDelete={() => updateParam("q", "")} size="small" /> : null}
          <ButtonLikeClear onClick={clearFilters} />
        </Stack>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <PostList
          posts={posts}
          emptyMessage={hasFilters ? "Ingen indlæg matcher filtrene." : "Ingen indlæg endnu."}
        />
      )}

      {isAdmin ? (
        <Fab
          color="primary"
          aria-label="Tilføj indlæg"
          onClick={() => setCreateOpen(true)}
          sx={{
            position: "fixed",
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <PostAddIcon />
        </Fab>
      ) : null}

      <PostFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        title="Nyt indlæg"
        submitLabel="Gem"
        initial={{ title: "", body: "", visibility: "private", composition: "journal", tags: "" }}
        postType="post"
      />
    </Box>
  );
}

function ButtonLikeClear({ onClick }) {
  return (
    <Typography
      component="button"
      type="button"
      onClick={onClick}
      variant="caption"
      sx={{
        border: 0,
        background: "none",
        cursor: "pointer",
        color: "primary.main",
        p: 0,
        font: "inherit",
      }}
    >
      Ryd alle
    </Typography>
  );
}

export default Home;
